(() => {
  if (customElements.get('video-text-carousel')) return;

  class VideoTextCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.sliderElement = this.querySelector('.video-text-carousel__swiper');
      this.wrapper = this.querySelector('.video-text-carousel__wrapper');
      this.realSlides = Array.from(this.querySelectorAll('.video-text-carousel__slide'));
      this.realSlideCount = this.realSlides.length;
      this.previousButton = this.querySelector('.video-text-carousel__nav-button--prev');
      this.nextButton = this.querySelector('.video-text-carousel__nav-button--next');
      this.autoplayToggle = this.querySelector('.video-text-carousel__autoplay-toggle');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.mobileMediaQuery = window.matchMedia('(max-width: 749px)');
      this.manualLoop = this.dataset.loop === 'true' && this.realSlideCount > 1;
      this.isNearViewport = false;

      if (!this.sliderElement || !this.wrapper || !this.realSlideCount) return;

      this.initialized = true;
      this.setupVisibilityObserver();
      this.setupResponsiveVideoListener();

      if (this.realSlideCount === 1) {
        this.classList.add('video-text-carousel--static');
        return;
      }

      if (this.manualLoop) this.addLoopClones();

      const enableAutoplay = this.dataset.autoplay === 'true' && !this.reduceMotion;

      this.swiper = new Swiper(this.sliderElement, {
        centeredSlides: true,
        slidesPerView: 'auto',
        initialSlide: this.manualLoop ? 1 : 0,
        spaceBetween: 0,
        speed: 700,
        loop: false,
        slideToClickedSlide: true,
        watchSlidesProgress: true,
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        autoplay: enableAutoplay
          ? {
              delay: Number(this.dataset.autoplayDelay) || 7000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }
          : false,
        on: {
          init: (swiper) => {
            this.updateNavigation(swiper);
            this.playActiveVideo();
          },
          slideChangeTransitionStart: () => this.pauseAllVideos(),
          slideChangeTransitionEnd: (swiper) => {
            this.normalizeLoop(swiper);
            this.updateNavigation(swiper);
            this.playActiveVideo();
          },
        },
      });

      this.setupNavigation();
      this.setupAutoplayToggle();
      this.addEventListener('shopify:block:select', this.handleBlockSelect);
    }

    disconnectedCallback() {
      this.visibilityObserver?.disconnect();
      this.mobileMediaQuery?.removeEventListener?.('change', this.handleResponsiveVideoChange);
      this.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.swiper?.destroy(true, true);
      this.swiper = null;
      this.initialized = false;
    }

    addLoopClones() {
      const firstClone = this.realSlides[0].cloneNode(true);
      const lastClone = this.realSlides[this.realSlideCount - 1].cloneNode(true);

      firstClone.classList.add('video-text-carousel__slide--clone');
      lastClone.classList.add('video-text-carousel__slide--clone');
      firstClone.removeAttribute('data-shopify-editor-block');
      lastClone.removeAttribute('data-shopify-editor-block');
      firstClone.setAttribute('aria-hidden', 'true');
      lastClone.setAttribute('aria-hidden', 'true');

      this.wrapper.insertBefore(lastClone, this.realSlides[0]);
      this.wrapper.appendChild(firstClone);
    }

    handleBlockSelect = (event) => {
      const slide = event.target.closest('.video-text-carousel__slide');
      if (!slide || !this.swiper) return;

      const realIndex = Number(slide.dataset.slideIndex);
      if (Number.isNaN(realIndex)) return;

      this.swiper.slideTo(realIndex + (this.manualLoop ? 1 : 0));
    };

    normalizeLoop(swiper) {
      if (!this.manualLoop) return;

      let resetIndex = null;

      if (swiper.activeIndex === 0) {
        resetIndex = this.realSlideCount;
      } else if (swiper.activeIndex === this.realSlideCount + 1) {
        resetIndex = 1;
      }

      if (resetIndex === null) return;

      this.classList.add('video-text-carousel--loop-resetting');
      swiper.slideTo(resetIndex, 0, false);
      swiper.updateSlidesClasses();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.classList.remove('video-text-carousel--loop-resetting');
        });
      });
    }

    getRealIndex(swiper = this.swiper) {
      if (!swiper) return 0;
      if (!this.manualLoop) return swiper.activeIndex;
      if (swiper.activeIndex === 0) return this.realSlideCount - 1;
      if (swiper.activeIndex === this.realSlideCount + 1) return 0;
      return swiper.activeIndex - 1;
    }

    setupNavigation() {
      this.previousButton?.addEventListener('click', () => this.swiper.slidePrev());
      this.nextButton?.addEventListener('click', () => this.swiper.slideNext());
    }

    updateNavigation(swiper = this.swiper) {
      if (!swiper || this.manualLoop) return;

      const updateButton = (button, disabled) => {
        if (!button) return;
        button.disabled = disabled;
        button.classList.toggle('video-text-carousel__nav-button--disabled', disabled);
      };

      updateButton(this.previousButton, swiper.isBeginning);
      updateButton(this.nextButton, swiper.isEnd);
    }

    setupAutoplayToggle() {
      if (!this.autoplayToggle || !this.swiper?.autoplay) return;

      this.autoplayToggle.addEventListener('click', () => {
        const isPaused = this.autoplayToggle.getAttribute('aria-pressed') === 'true';

        if (isPaused) {
          this.swiper.autoplay.start();
          this.autoplayToggle.setAttribute('aria-pressed', 'false');
          this.autoplayToggle.setAttribute('aria-label', 'Pause carousel');
        } else {
          this.swiper.autoplay.stop();
          this.autoplayToggle.setAttribute('aria-pressed', 'true');
          this.autoplayToggle.setAttribute('aria-label', 'Play carousel');
        }
      });
    }

    setupVisibilityObserver() {
      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.isNearViewport = true;
              this.playActiveVideo();
            } else {
              this.isNearViewport = false;
              this.pauseAllVideos();
            }
          });
        },
        { rootMargin: '300px 0px', threshold: 0 }
      );

      this.visibilityObserver.observe(this);
    }

    setupResponsiveVideoListener() {
      this.mobileMediaQuery.addEventListener?.('change', this.handleResponsiveVideoChange);
    }

    handleResponsiveVideoChange = () => {
      if (!this.isNearViewport) return;
      this.playActiveVideo();
    };

    pauseAllVideos() {
      this.querySelectorAll('video').forEach((video) => video.pause());
    }

    hydrateVideo(video) {
      if (!video || video.dataset.hydrated === 'true') return;

      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
      });
      video.dataset.hydrated = 'true';
      video.load();
    }

    playActiveVideo() {
      if (!this.isNearViewport) return;

      this.pauseAllVideos();

      const activeSlide = this.querySelector('.swiper-slide-active') || this.realSlides[0];
      if (!activeSlide) return;

      const useMobileVideo = this.mobileMediaQuery.matches;
      const mobileVideo = activeSlide.querySelector('.video-text-carousel__video--mobile');
      const desktopVideo = activeSlide.querySelector('.video-text-carousel__video--desktop');
      const video = useMobileVideo && mobileVideo ? mobileVideo : desktopVideo;

      this.hydrateVideo(video);

      if (this.dataset.videoAutoplay === 'true' && !this.reduceMotion) {
        video?.play().catch(() => {});
      }
    }
  }

  customElements.define('video-text-carousel', VideoTextCarousel);
})();
